(ns kyber.lib.pdftron.get-visible-annotations
  (:require [goog.object :as gobj]))

;; Here is a small overview of how `annotation-in-rect` behaves.
;; r- : viewport area of pdf
;; ar- : annotation area
;; 1 is the top area, e.g r-y2 is the top area of the viewport
;; so 2 is the bottom area.
;; keep in mind the WV numbers increase as you scroll down.

;; 1 top of webview
;; |
;; 2 bottom of webview

;; 2 > 1 top number less then bottom e.g as you scroll down number increases

;; so lets say were trying to see if our annotation in the upper forth e.g

;; r1 top of webview
;; ar-1
;; ar-2a - 1/4 of wv
;; | - 3/4s of screen
;; r2 bottom of webview

;; we would be moving the bottom VW up (subtracting, sense the numbers increase as go down). which is what we see in the logic 

;; (let [one-fourth (/ (- r-y2 r-y1) 4)] ;; get one fourth of the area of the WV
;;   (annotation-in-rect? (assoc params :r-y2 (- r-y2 (* one-fourth 3))))) ;; then move the WV up 3 times 

;; so we go from:

;; r1 top of webview
;; |
;; |
;; r2 bottom of webview

;; to

;; r1 top of webview
;; |
;; new-r2 bottom of webview
;; |


(defn- annotation-in-rect? [{:keys [ar-x1 ar-y1 ar-x2 ar-y2
                                    r-x1 r-y1 r-x2 r-y2
                                    page-margin y-offset]}]
  (and (< r-x1 (+ ar-x2 page-margin))
       (> r-x2 (+ ar-x1 page-margin))
       (< r-y1 (+ ar-y2 y-offset))
       (> r-y2 (+ ar-y1 y-offset))))

(def is-annotation-in-viewport? annotation-in-rect?)

(defn- is-annotation-in-upper-fourth-of-viewport?
  "Check to see if a given annotation rect is in the upper fourth of the
  viewport"
  [{:keys [r-y1 r-y2] :as params}]
  (let [one-fourth (/ (- r-y2 r-y1) 4)]
    (annotation-in-rect? (assoc params :r-y2 (- r-y2 (* one-fourth 3))))))

(defn- is-annotation-in-upper-third-of-viewport?
  "Check to see if a given annotation rect is in the upper third of the
  viewport"
  [{:keys [r-y1 r-y2] :as params}]
  (let [one-third (/ (- r-y2 r-y1) 3)]
    (annotation-in-rect? (assoc params :r-y2 (- r-y2 (* one-third 2))))))

(defn- is-annotation-in-middle-third-of-viewport?
  "Check to see if a given annotation rect is in the middle third of the
  viewport"
  [{:keys [r-y1 r-y2] :as params}]
  (let [one-third (/ (- r-y2 r-y1) 3)]
    (annotation-in-rect? (assoc params
                                :r-y1 (+ r-y1 one-third)
                                :r-y2 (- r-y2 one-third)))))

(defn- is-annotation-in-lower-third-of-viewport?
  "Check to see if a given annotation rect is in the lower third of the
  viewport"
  [{:keys [r-y1 r-y2] :as params}]
  (let [one-third (/ (- r-y2 r-y1) 3)]
    (annotation-in-rect? (assoc params
                                :r-y1 (+ r-y1 (* one-third 2))))))

(defn- is-in-lower-three-fourths? 
  [{:keys [r-y1 r-y2] :as params}]
  (let [one-fourth (/ (- r-y2 r-y1) 4)]
    (annotation-in-rect? (assoc params
                                :r-y1 (+ r-y1 one-fourth)))))

;; The linter hates this inline and just using max
;; does not work using js reduce.
(defn- get-max [p1 p2]
  (max p1 p2))

(defn get-visible-annotations
  "Returns a set js list of visible annotaitons"
  [{:keys [doc-viewer annotation-manager annotations internal-state]}
   {:keys [x1 y1 x2 y2]}]
  (when (true? (:annotation-layer-loaded @internal-state))
    (let [visible-annotations
          (atom {:in-viewport []
                 :in-upper-fourth []
                 :in-upper-third []
                 :in-middle-third []
                 :in-lower-third []
                 :in-lower-three-fourths []})

          {:keys [visible-pages]}
          @internal-state

          Rect
          (gobj/get annotations "Rect")

          annotation-list
          (js-invoke annotation-manager "getAnnotationsList")

          page-margin
          4

          viewport-rect
          (new Rect x1 y1 x2 y2)

          r-x1
          (gobj/get viewport-rect "x1")

          r-y1
          (gobj/get viewport-rect "y1")

          r-x2
          (gobj/get viewport-rect "x2")

          r-y2
          (gobj/get viewport-rect "y2")

          max-page-number
          (inc (js-invoke visible-pages "reduce" get-max 0))

          {:keys [page-offsets]}
          (reduce
           (fn [{:keys [current-offset] :as acc} page-number]
             (let [rotation (js-invoke doc-viewer "getCompleteRotation" page-number)
                   page-height (if (#{1 3} rotation)
                                 (js-invoke doc-viewer "getPageWidth" (dec page-number))
                                 (js-invoke doc-viewer "getPageHeight" (dec page-number)))]
               (-> acc
                   (assoc-in [:page-offsets page-number]
                             (+ current-offset page-margin))
                   (update :current-offset
                           #(+ % page-height (* page-margin 2))))))
           {:current-offset 0 :page-offsets {}}
           (range 1 (inc max-page-number)))]

      (js-invoke
       annotation-list
       "forEach"
       (fn [annotation]
         (let [page-number (gobj/get annotation "PageNumber")
               page-offset (get page-offsets page-number)
               annotation-rect (js-invoke annotation "getRect")
               is-on-page (js-invoke visible-pages
                                     "includes"
                                     (dec page-number))
               is-link (= (gobj/get annotation "elementName") "link")
               viewport-check-args {:ar-x1 (gobj/get annotation-rect "x1")
                                    :ar-y1 (gobj/get annotation-rect "y1")
                                    :ar-x2 (gobj/get annotation-rect "x2")
                                    :ar-y2 (gobj/get annotation-rect "y2")
                                    :r-x1 r-x1
                                    :r-y1 r-y1
                                    :r-x2 r-x2
                                    :r-y2 r-y2
                                    :page-margin page-margin
                                    :y-offset page-offset}
               is-in-viewport (and (true? is-on-page)
                                   (false? is-link)
                                   (is-annotation-in-viewport?
                                     viewport-check-args))]

           (when (and is-in-viewport
                   (is-in-lower-three-fourths? viewport-check-args))
             (swap! visible-annotations update
                    :in-lower-three-fourths conj annotation))

           (when (true? is-in-viewport)
             (swap! visible-annotations update
                    :in-viewport conj annotation))

           (when (and (true? is-in-viewport)
                      (is-annotation-in-upper-fourth-of-viewport?
                       viewport-check-args))
             (swap! visible-annotations update
                    :in-upper-fourth conj annotation))

           (when (and (true? is-in-viewport)
                      (is-annotation-in-upper-third-of-viewport?
                       viewport-check-args))
             (swap! visible-annotations update
                    :in-upper-third conj annotation))

           (when (and (true? is-in-viewport)
                      (is-annotation-in-middle-third-of-viewport?
                       viewport-check-args))
             (swap! visible-annotations update
                    :in-middle-third conj annotation))

           (when (and (true? is-in-viewport)
                      (is-annotation-in-lower-third-of-viewport?
                       viewport-check-args))
             (swap! visible-annotations update
                    :in-lower-third conj annotation)))))

      @visible-annotations)))
